import React, { useState, useRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import { TextInput } from "../textInput";
import "react-datepicker/dist/react-datepicker.css";
import { LeftHalfArrow } from "helper/icons";
import { calendar, close, helpInfo } from "helper/icons";
import moment from "moment";
import { globalVariables } from "helper/globalVariables";

export const CustomDateRangePicker = ({
  isShadow = false,
  onSetDate = () => {},
  width = "w-[264px] lg:w-[270px] max-[400px]:w-[100%]",
  isNavigation = false,
  defaultStartDate = null,
  defaultEndDate = null,
}) => {
  const inputRef = useRef(null);
  const datePickerRef = useRef(null);
  const customHeaderRef = useRef(null);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [isHovered, setIsHovered] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [view, setView] = useState("calendar");
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleChange = ([newStartDate, newEndDate]) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    if (newStartDate && newEndDate) {
      const formattedStart = moment(newStartDate).format("MM-YYYY");
      const formattedEnd = moment(newEndDate).format("MM-YYYY");
      setInputValue(`${formattedStart} - ${formattedEnd}`);
      onSetDate(newStartDate, newEndDate);
      setShowDatePicker(false);
    }
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  const clearSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setInputValue("");
    onSetDate(null, null);
  };

  useEffect(() => {
    if (defaultStartDate && defaultEndDate) {
      const formattedStart = moment(defaultStartDate).format("MM-YYYY");
      const formattedEnd = moment(defaultEndDate).format("MM-YYYY");
      setInputValue(`${formattedStart} - ${formattedEnd}`);
    } else {
      setStartDate(null);
      setEndDate(null);
      setInputValue("");
      onSetDate(null, null);
    }
  }, [defaultStartDate, defaultEndDate]);

  useEffect(() => {
    const listBoxElement = datePickerRef.current?.querySelector('[role="listbox"]');
    if (listBoxElement) {
      listBoxElement.style.display = view === "calendar" ? "" : "none";
    }
  }, [view]);

  const icon = inputValue ? close : calendar;

  const handleIconClick = () => {
    inputValue ? clearSelection() : toggleDatePicker();
  };

  return (
    <div className="relative group">
      <div
        ref={datePickerRef}
        className={`relative flex items-center bg-white border rounded-lg ${
          isShadow ? "shadow-100" : ""
        } ${width}`}
      >
        <TextInput
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={globalVariables.selectDates}
          width="w-full"
          disabled={true}
        />
        <img
          src={icon}
          alt={inputValue ? "Close" : "Calendar"}
          height={20}
          width={20}
          className="absolute right-2 cursor-pointer"
          onClick={handleIconClick}
        />
        {isNavigation && (
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img src={helpInfo} alt="Help" className="cursor-pointer absolute top-[14px] right-9" />
            {isHovered && (
              <div className="absolute -bottom-32 right-0 w-64 p-3 bg-white shadow-100 border border-gray-300 rounded-lg z-[99999]">
                <h3 className="text-sm font-semibold text-gray-800">📅 How to Use</h3>
                <ul className="list-disc pl-4 text-xs text-gray-700">
                  <li>Click the calendar icon to open.</li>
                  <li>Click on the Year to choose a year.</li>
                  <li>Use arrows to navigate months.</li>
                  <li>Click on the X to reset.</li>
                </ul>
              </div>
            )}
          </div>
        )}
        {showDatePicker && (
          <DatePicker
            selected={startDate}
            onChange={handleChange}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            dateFormat="MM-yyyy"
            onClickOutside={() => setShowDatePicker(false)}
            showMonthYearPicker
            inline
            renderCustomHeader={({
              date,
              changeYear,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => {
              const currentYear = date.getFullYear();
              const startYear = currentYear - 6;
              const endYear = currentYear + 5;
              return (
                <div ref={customHeaderRef} className="custom-header flex justify-between items-center">
                  {view === "calendar" ? (
                    <>
                      <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                        <LeftHalfArrow />
                      </button>
                      <button className="toggle-button text-gray-900 font-bold" onClick={() => setView("years")}>
                        {date.getFullYear()}
                      </button>
                      <button onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
                        <LeftHalfArrow style={{ transform: "rotate(180deg)" }} />
                      </button>
                    </>
                  ) : (
                    <div className="flex-1">
                    <div className="flex justify-between">
                      <button
                        className="arrow-button"
                        onClick={() => changeYear(startYear - 12)}
                      >
                        <LeftHalfArrow />
                      </button>
                      <button
                        className="arrow-button"
                        onClick={() => changeYear(endYear + 1)}
                      >
                        <LeftHalfArrow style={{ transform: "rotate(180deg)" }} />
                      </button>
                    </div>
                      <div className="year-selector">
                        {Array.from({ length: 12 }, (_, i) => startYear + i).map((year) => (
                          <div
                            key={year}
                            className="year-item"
                            onClick={() => {
                              changeYear(year);
                              setView("calendar");
                            }}
                          >
                            {year}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};